import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, Thermometer, ClipboardList, AlertTriangle, Smartphone } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd, KeywordCrossLinks } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Mi az a HACCP igazolás / HACCP dokumentáció?',
    a: 'A HACCP (Hazard Analysis and Critical Control Points) az élelmiszer-biztonsági veszélyelemzésen alapuló, jogszabály által kötelező rendszer. A HACCP dokumentáció tartalmazza a veszélyelemzést, a kritikus szabályozási pontokat (CCP), a monitoring rendszert és a napi ellenőrzési naplókat — ez az, amit a hatóság ellenőrzéskor kérhet.',
  },
  {
    q: 'Milyen kritikus pontokat (CCP) kezel a rendszer?',
    a: 'A CukrászDoc a jellemző cukrászati CCP-ket dolgozza fel automatikusan: sütés/hőkezelés (min. belső hőmérséklet, idő) és hűtés (hűtési idő és céghőmérséklet), a hozzájuk tartozó monitoring és korrekciós lépésekkel együtt.',
  },
  {
    q: 'Elfogadja a hatóság a digitális HACCP naplót?',
    a: 'Igen, a HACCP dokumentáció megfelel a magyar jogszabályi elvárásoknak. A digitális napló ugyanazokat az adatokat tartalmazza, mint a papíralapú, csak könnyebben vezethető és mindig elérhető ellenőrzéskor.',
  },
  {
    q: 'Muszáj papíron is vezetni a HACCP naplót?',
    a: 'Nem. A hűtő hőmérsékletek, a takarítási és termelési napló telefonról vagy tabletről is vezethető, nem kell hozzá kockás füzet vagy nyomtatott ellenőrzőlista.',
  },
  {
    q: 'Egy konkrét szállítmányhoz kérnek "HACCP igazolást" — ez ugyanez?',
    a: 'Ha egy megrendelő (óvoda, iskola, rendezvényszervező) egyetlen szállított tételhez kér "HACCP igazolást", azzal jellemzően nem a teljes HACCP dokumentációra, hanem egy termékspecifikus eredetigazolásra gondol. Ezt a CukrászDoc-ban az eredetigazolás (származási igazolás) oldalon tudod egy kattintással legenerálni, ugyanabból a receptadatból.',
  },
]

export default function HaccpIgazolasPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>HACCP igazolás és napló online — CukrászDoc</title>
        <meta name="description" content="HACCP dokumentáció és digitális napló automatikusan, receptből. Veszélyelemzés, CCP-k, napi ellenőrzőlista — hatósági ellenőrzésre kész, telefonról is vezethető." />
        <link rel="canonical" href="https://cukraszdoc.hu/haccp-igazolas" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <Thermometer size={12} />
            Felejtsd el a kockás füzetet
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight text-white mb-5">
            HACCP igazolás <span style={{ color: TEAL }}>és napló egy helyen</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Veszélyelemzés, kritikus szabályozási pontok (CCP) és napi ellenőrzőlista automatikusan a recept adataiból — a HACCP dokumentáció hatósági ellenőrzésre kész, a napló pedig telefonról is vezethető.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all" style={{ background: TEAL, color: '#fff' }}>
              Próbáld ki ingyen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6" style={{ color: DARK }}>Mit tartalmaz a HACCP dokumentáció?</h2>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            A HACCP terv a veszélyelemzésből indul (pl. alapanyag-átvétel, sütés/hőkezelés, hűtés, csomagolás), és minden lépéshez megjelöli, hogy kritikus szabályozási pont (CCP)-e. A tipikus cukrászati CCP-k a hőkezelés (min. belső hőmérséklet 72°C, 2 percig) és a hűtés (4 óra alatt 8°C alá).
          </p>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            Ehhez tartozik a monitoring rendszer (mit, mikor, hogyan mérünk) és a napi ellenőrzési lista — hűtő hőmérsékletek reggel-este, takarítási és termelési napló. A CukrászDoc ezt a teljes dokumentációt automatikusan generálja a recept adatai alapján, a napi naplókat pedig digitálisan, telefonról is vezetheted.
          </p>
          <p className="text-base leading-relaxed text-gray-600">
            Ha egy konkrét szállítmányhoz (óvodának, iskolának, rendezvényre) kérnek tőled "HACCP igazolást", azt jellemzően a <Link to="/szarmazasi-igazolas" style={{ color: TEAL, fontWeight: 600 }}>eredetigazolás</Link> oldalon tudod egy kattintással legenerálni — arról, hogy pontosan mit szoktak kérni "HACCP igazolás" néven, egy rövid összefoglalót is írtunk: <Link to="/blog/mit-jelent-a-haccp-igazolas" style={{ color: TEAL, fontWeight: 600 }}>mit jelent valójában a HACCP igazolás?</Link> Ha pedig alapanyag-szintű nyomonkövetést keresel, azt QR-kóddal, drága hardver nélkül is meg lehet oldani — <Link to="/blog/alapanyag-nyomonkoveres-qr-koddal" style={{ color: TEAL, fontWeight: 600 }}>itt mutatjuk be, hogyan</Link>.
          </p>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: DARK }}>A HACCP modul részei</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: AlertTriangle, title: 'Veszélyelemzés & CCP-k', desc: 'Sütés/hőkezelés, hűtés — automatikusan azonosítva.' },
              { icon: ClipboardList, title: 'Monitoring rendszer', desc: 'Mit, mikor, hogyan ellenőrzünk és mi a korrekció.' },
              { icon: Thermometer, title: 'Napi ellenőrzőlista', desc: 'Hűtő hőmérséklet reggel-este, takarítás, termelés.' },
              { icon: Smartphone, title: 'Digitális napló', desc: 'Telefonról, tabletről vezethető, mindig elérhető.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <Icon size={22} style={{ color: TEAL }} className="mb-3" />
                <h3 className="font-semibold mb-2 text-base" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések a HACCP-ről</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <KeywordCrossLinks current="/haccp-igazolas" />

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Kezdd el ma — ingyen</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Regisztrálj, és percek alatt kész a HACCP dokumentációd.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            <Check size={17} /> Ingyenes regisztráció
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
